import { Box, TextInput, Select, Text, Flex, Button, FileButton, Image, Tooltip, Grid } from "@mantine/core";
import { IconBuilding, IconLock, IconInfoCircle } from "@tabler/icons-react";
import {
  countriesData,
  getRegistrationNameByCountry,
  HEADQUARTERS_COUNTRY_PHONE_ISO,
  THE_KYB_US_STATE_CODES,
} from "../../../components/shared/constants/general.const";
import { getFileUrl } from "@/shared/utils/useFileUrl";
import { useState, useEffect, useRef } from "react";
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";
import { errorNotification } from "@/shared/utils/errorNotification";
import { VERIFICATION_BUSINESS_TYPE_SELECT_DATA } from "./verificationDocuments";

interface BasicCompanyInfoProps {
  formData: {
    companyLegalName: string;
    tradeName: string;
    headquartersCity: string;
    headquartersCountry: string;
    registrationNumber: string;
    registrationIdType: string | null;
    companyWebsite: string;
    phoneNumber: string;
    state?: string;
    phoneCountryCode: string;
    verificationBusinessType?: string | null;
  };
  updateForm: (field: string, value: any) => void;
  logo: File | null;
  setLogo: (file: File | null) => void;
  countryOptions: { group: string; items: string[] }[];
  existingLogo?: any;
  onRemoveLogo?: () => void;
  isFieldLocked?: (fieldName: string) => boolean;
  rightOfLogoContent?: React.ReactNode;
}

export default function BasicCompanyInfo({
  formData,
  updateForm,
  logo,
  setLogo,
  countryOptions,
  existingLogo,
  onRemoveLogo,
  isFieldLocked = () => false,
  rightOfLogoContent,
}: BasicCompanyInfoProps) {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoInputRef = useRef<() => void>(null);

  const lockedTooltip = "This field cannot be edited after verification";
  const normalizedCountry = formData.headquartersCountry?.trim().toLowerCase();
  const isUSA = normalizedCountry === "usa" || normalizedCountry === "united states";
  useEffect(() => {
    let mounted = true;

    const loadLogoPreview = async () => {
      if (logo) {
        const preview = URL.createObjectURL(logo);
        if (mounted) setLogoPreview(preview);
        return () => URL.revokeObjectURL(preview);
      } else if (existingLogo?.id) {
        try {
          const url = await getFileUrl(existingLogo.id);
          if (mounted) setLogoPreview(url);
        } catch (err) {
          console.error("Failed to load logo preview:", err);
          if (mounted) setLogoPreview(null);
        }
      } else {
        if (mounted) setLogoPreview(null);
      }
    };

    loadLogoPreview();

    return () => {
      mounted = false;
    };
  }, [logo, existingLogo]);

  /* ---------------- Registration ID Logic ---------------- */
  const isIndia = formData.headquartersCountry === "India";
  const usesLeiField = formData.registrationIdType === "LEI";
  const countryOfficialRegistryLabel =
    (formData.headquartersCountry && getRegistrationNameByCountry(formData.headquartersCountry)) ||
    "Official company registration ID";
  const phoneDefaultCountry =
    (formData.headquartersCountry && HEADQUARTERS_COUNTRY_PHONE_ISO[formData.headquartersCountry]) || "in";

  const getRegistrationIdType = (headquartersCountry: string, selectedType?: string) => {
    if (selectedType === "LEI") return "LEI";
    if (selectedType === "LOCAL_REGISTRY") {
      return (
        (headquartersCountry && getRegistrationNameByCountry(headquartersCountry)) || "Official registration ID"
      );
    }
    if (headquartersCountry === "India" && selectedType) {
      return selectedType; // GST, CIN, UDM
    }
    if (!headquartersCountry) return "Registration Number";
    for (const group of countriesData.countryGroups) {
      const countryItem = group.items.find((item) => item.name === headquartersCountry);
      if (countryItem) {
        return countryItem.registrationName;
      }
    }
    return "Registration Number";
  };

  const getRegistrationPlaceholder = (headquartersCountry: string, selectedType?: string) => {
    if (!selectedType) return "Select registration type first";
    if (selectedType === "LEI") return "20-character LEI";
    if (headquartersCountry === "India") {
      return `Enter ${selectedType}`;
    }
    if (selectedType === "LOCAL_REGISTRY") {
      return `Enter ${countryOfficialRegistryLabel}`;
    }
    return `Enter ${getRegistrationIdType(headquartersCountry, selectedType)}`;
  };

  const resetIdentifiersForCountryChange = (value: string | null) => {
    if (value === "India") {
      updateForm("registrationIdType", "LEI");
      updateForm("registrationNumber", "");
    } else if (value) {
      updateForm("registrationIdType", "LEI");
      updateForm("registrationNumber", "");
    } else {
      updateForm("registrationIdType", null);
      updateForm("registrationNumber", "");
    }
  };

  // Helper to render label with optional lock icon
  const renderLabel = (label: string, fieldName: string, required = false, extra?: React.ReactNode) => (
    <Flex align="center" gap={4}>
      <Text fz="sm" fw={500}>
        {label}
        {required && (
          <Text span c="red" fz="lg">
            {" "}
            *
          </Text>
        )}
        {extra}
      </Text>
      {isFieldLocked(fieldName) && (
        <Tooltip label={lockedTooltip} withArrow position="top">
          <Flex align="center">
            <IconLock size={12} color="#9CA3AF" />
          </Flex>
        </Tooltip>
      )}
    </Flex>
  );

  return (
    <Box>
      {/* Company Logo - Always editable */}
      <Box mb="xl">
        <Text visibleFrom="md" fz="sm" fw={500} mb="xs">
          Company Logo
        </Text>

        {/* Mobile: show boxes above logo */}
        {rightOfLogoContent && (
          <Box hiddenFrom="md" mb="md">
            {rightOfLogoContent}
          </Box>
        )}

        <Text hiddenFrom="md" fz="sm" fw={500} mb="xs">
          Company Logo
        </Text>

        {/* Desktop: logo left, boxes right */}
        <Flex gap="md" align="center">
          <Box
            style={{
              width: 96,
              height: 96,
              borderRadius: 8,
              overflow: "hidden",
              border: "1px solid #E5E7EB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#F8FAFC",
            }}
          >
            {logoPreview ? (
              <Image
                src={logoPreview}
                alt="Company Logo"
                fit="cover"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  console.error("❌ Logo failed to load:", logoPreview);
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <IconBuilding size={28} color="#94A3B8" />
            )}
          </Box>
          <Box>
            <Flex gap="xs">
              <FileButton
                resetRef={logoInputRef}
                onChange={(file) => {
                  if (file) {
                    const maxSize = 2 * 1024 * 1024; // 2MB
                    if (file.size > maxSize) {
                      errorNotification({
                        title: "File too large",
                        message: "Logo size must be less than 2MB",
                      });
                      logoInputRef.current?.();
                      return;
                    }
                    setLogo(file);
                    logoInputRef.current?.();
                  }
                }}
                accept="image/png,image/jpeg"
              >
                {(props) => (
                  <Button variant="outline" size="xs" {...props}>
                    Upload Logo
                  </Button>
                )}
              </FileButton>

              {(logo || logoPreview) && (
                <Button
                  variant="outline"
                  size="xs"
                  color="red"
                  onClick={() => {
                    if (onRemoveLogo) {
                      onRemoveLogo();
                    } else {
                      setLogo(null);
                      setLogoPreview(null);
                    }
                  }}
                >
                  Remove
                </Button>
              )}
            </Flex>
            <Text fz="xs" c="slate.6" mt={6}>
              PNG, JPG up to 2MB. Recommended: 400×400px square logo
            </Text>
          </Box>
          {rightOfLogoContent && (
            <Box visibleFrom="md" style={{ marginLeft: "auto", minWidth: 260, maxWidth: "100%" }}>
              {rightOfLogoContent}
            </Box>
          )}
        </Flex>
      </Box>

      <Grid gutter="md" mb="md">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput
            label={renderLabel("Company Legal Name", "companyLegalName", true)}
            placeholder="Enter registered company name"
            value={formData.companyLegalName}
            onChange={(e) => updateForm("companyLegalName", e.currentTarget.value)}
            disabled={isFieldLocked("companyLegalName")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput
            label={
              <Text fz="sm" fw={500}>
                Trade Name / DBA{" "}
              </Text>
            }
            placeholder="Public brand name"
            value={formData.tradeName}
            onChange={(e) => updateForm("tradeName", e.currentTarget.value)}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Box>
            <Text fz="sm" fw={500} mb={4}>
              Company Website
              <Text span c="red" fz="lg">
                {" "}
                *
              </Text>
            </Text>
            <TextInput
              placeholder="https://www.company.com"
              value={formData.companyWebsite}
              onChange={(e) => updateForm("companyWebsite", e.currentTarget.value)}
              error={
                formData.companyWebsite &&
                  !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.companyWebsite)
                  ? "Please enter a valid website URL"
                  : null
              }
            />
          </Box>
        </Grid.Col>
      </Grid>

      <Grid gutter="md" mb="xs">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput
            label={renderLabel("City", "headquartersCity", true)}
            placeholder="Enter city"
            value={formData.headquartersCity}
            onChange={(e) => updateForm("headquartersCity", e.currentTarget.value)}
            disabled={isFieldLocked("headquartersCity")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Select
            label={renderLabel("Country", "headquartersCountry", true)}
            placeholder="Select country"
            data={countryOptions}
            value={formData.headquartersCountry}
            onChange={(value) => {
              updateForm("headquartersCountry", value);
              updateForm("country", value);
              resetIdentifiersForCountryChange(value ?? null);
              if (value?.trim().toLowerCase() !== "usa" && value?.trim().toLowerCase() !== "united states") {
                updateForm("state", "");
              }
            }}
            searchable
            disabled={isFieldLocked("headquartersCountry")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput
            label={
              <Text fz="sm" fw={500}>
                Phone Number
                <Text span c="red" fz="lg">
                  {" "}
                  *
                </Text>
              </Text>
            }
            placeholder="hidden"
            styles={{
              input: {
                display: "none",
              },
              wrapper: {
                display: "none",
              },
              root: {
                marginBottom: 0,
              },
            }}
            inputContainer={() => (
              <PhoneInput
                key={phoneDefaultCountry}
                defaultCountry={phoneDefaultCountry}
                value={`${formData.phoneCountryCode}${formData.phoneNumber}`}
                onChange={(phone, meta) => {
                  const countryCode = meta.country?.dialCode ? `+${meta.country.dialCode}` : "";
                  const nationalNumber = phone.replace(`+${meta.country?.dialCode || ""}`, "").trim();
                  updateForm("phoneCountryCode", countryCode);
                  updateForm("phoneNumber", nationalNumber);
                }}
                placeholder="123-456-7890"
                style={{
                  width: "100%",
                }}
                inputStyle={{
                  width: "100%",
                  height: "36px",
                  fontSize: "14px",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderLeft: "none",
                  borderRadius: "0 var(--mantine-radius-md) var(--mantine-radius-md) 0",
                  outline: "none",
                }}
                countrySelectorStyleProps={{
                  buttonStyle: {
                    height: "36px",
                    border: "1px solid var(--mantine-color-gray-3)",
                    borderRadius: "var(--mantine-radius-md) 0 0 var(--mantine-radius-md)",
                    padding: "0 12px",
                    backgroundColor: "white",
                  },
                }}
              />
            )}
          />
        </Grid.Col>

        {isUSA && (
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Select
              label="State"
              placeholder="Select state"
              data={THE_KYB_US_STATE_CODES.map((item) => ({
                value: item.name,
                label: item.name,
              }))}
              value={formData.state || null}
              onChange={(value) => updateForm("state", value)}
              required
            />
          </Grid.Col>
        )}
      </Grid>
      <Flex align="center" mb="md">
        <IconInfoCircle size={14} color="var(--mantine-color-primary-7)" />
        <Text fz="xs" c="primary" ml={2}>
          Registration identifier is auto-selected based on country:{" "}
          {formData.headquartersCountry
            ? isIndia
              ? "LEI (Recommended) is selected by default. You can switch to GST/CIN/Udyam. Verification happens on your profile."
              : "LEI (Recommended) is selected by default. You can switch to your country’s official ID (e.g. NIPC). Verification happens on your profile."
            : "Select country, then registration type."}
        </Text>
      </Flex>

      <Grid gutter="md" mb="md">
        <Grid.Col span={{ base: 12, md: 4 }}>
          {isIndia && (
            <Select
              label={renderLabel("Registration ID Type", "registrationIdType", true)}
              placeholder="Select Type"
              data={[
                { value: "LEI", label: "LEI (Recommended)" },
                { value: "GST", label: "GST" },
                { value: "CIN", label: "CIN" },
                { value: "UDM", label: "UDM (Udyam)" },
              ]}
              value={formData.registrationIdType}
              onChange={(value) => {
                updateForm("registrationIdType", value);
                updateForm("registrationNumber", "");
              }}
              disabled={isFieldLocked("registrationIdType")}
            />
          )}

          {!isIndia && formData.headquartersCountry && (
            <Select
              label={renderLabel("Registration ID Type", "registrationIdType", true)}
              placeholder="Select type"
              data={[
                { value: "LEI", label: "LEI (Recommended)" },
                {
                  value: "LOCAL_REGISTRY",
                  label: countryOfficialRegistryLabel,
                },
              ]}
              value={formData.registrationIdType}
              onChange={(value) => {
                updateForm("registrationIdType", value);
                updateForm("registrationNumber", "");
              }}
              disabled={isFieldLocked("registrationIdType")}
            />
          )}
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          {/* Registration / LEI value — verification runs on profile page */}
          <TextInput
            label={renderLabel(
              getRegistrationIdType(formData.headquartersCountry, formData.registrationIdType ?? undefined),
              "registrationNumber",
              true
            )}
            placeholder={getRegistrationPlaceholder(
              formData.headquartersCountry,
              formData.registrationIdType ?? undefined
            )}
            maxLength={usesLeiField ? 20 : undefined}
            inputMode={usesLeiField ? "text" : undefined}
            value={formData.registrationNumber}
            onChange={(e) => {
              const v = e.currentTarget.value;
              if (usesLeiField) {
                updateForm("registrationNumber", v.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 20));
              } else {
                updateForm("registrationNumber", v);
              }
            }}
            disabled={
              isFieldLocked("registrationNumber") ||
              !formData.headquartersCountry ||
              !formData.registrationIdType
            }
            error={
              usesLeiField && formData.registrationNumber
                ? formData.registrationNumber.trim().length === 20
                  ? null
                  : "LEI must be exactly 20 characters"
                : null
            }
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Select
            label="Business structure"
            placeholder="Select type"
            data={VERIFICATION_BUSINESS_TYPE_SELECT_DATA}
            value={formData.verificationBusinessType || null}
            onChange={(value) => updateForm("verificationBusinessType", value || "")}
            disabled={isFieldLocked("verificationBusinessType")}
          />
        </Grid.Col>
      </Grid>

      {/* Company Website and Phone Number are positioned above in the 3-column rows */}
    </Box>
  );
}
